import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import OperationalKpiCards from './components/OperationalKpiCards';
import ExportCenter from './components/ExportCenter';
import WorkflowStatusBoard from './components/WorkflowStatusBoard';
import OperationalActivityTable from './components/OperationalActivityTable';
import StatusBreakdownChart from './components/StatusBreakdownChart';
import { fetchOperationalSummary } from './api/govopsOperationsService';
import { formatGovOpsError } from './api/govopsErrors';
import { getPermitMapPosition } from './utils/mapCoordinates';
import {
  emptyQuickIntakeForm,
  INTAKE_MODES,
  INTAKE_SOURCES,
} from './utils/permitIntake';
import { usePermitIntake } from './hooks/usePermitIntake';
import PermitQuickIntakeForm from './components/PermitQuickIntakeForm';
import { formatStatus, formatTimestamp } from './constants/workflowLabels';
import PageShell from './components/PageShell';
import PilotOutreachBanner from './components/PilotOutreachBanner';
import PilotDemoNavLinks from './components/PilotDemoNavLinks';
import GlobalSearch from './components/GlobalSearch';

const AdminDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSubmit, setShowSubmit] = useState(false);

  const [quickForm, setQuickForm] = useState(emptyQuickIntakeForm);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setSummary(await fetchOperationalSummary());
    } catch (e) {
      setError(formatGovOpsError(e));
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const {
    submit: submitIntake,
    submitting,
    error: submitError,
    success: submitSuccess,
  } = usePermitIntake({
    source: INTAKE_SOURCES.ADMIN,
    mode: INTAKE_MODES.QUICK,
    onSuccess: async () => {
      setQuickForm(emptyQuickIntakeForm());
      await load();
    },
  });

  const handleQuickSubmit = async (e) => {
    e.preventDefault();
    try {
      await submitIntake(quickForm);
    } catch (_) {
      /* error surfaced via hook */
    }
  };

  const permits = summary?.permits || [];
  const recentCases = summary?.recentCases || [];
  const isEmpty =
    summary &&
    permits.length === 0 &&
    (summary.cases?.length || 0) === 0 &&
    (summary.violations?.length || 0) === 0;

  return (
    <PageShell
      title="Operations dashboard"
      subtitle="RobinhoodSpace false-alarm permit operations — governed by SD Panthers GovOps."
      className="rhs-admin-dashboard"
    >
      <header className="rhs-admin-hero">
        <div className="rhs-admin-hero-text">
          <p className="rhs-admin-eyebrow">RobinhoodSpace · GovOps pilot</p>
          <p className="rhs-muted">
            Executive visibility into permits, cases, violations, and workflow status for
            operational demos.
          </p>
        </div>
        <div className="rhs-admin-hero-actions">
          <button
            type="button"
            className="rhs-btn rhs-btn--secondary"
            onClick={load}
            disabled={loading}
          >
            {loading ? 'Refreshing…' : 'Refresh data'}
          </button>
        </div>
      </header>

      <PilotOutreachBanner />

      <nav className="rhs-quick-nav" aria-label="Admin quick links">
        <PilotDemoNavLinks />
        <Link to="/map" className="rhs-quick-nav-item">
          Permit map
        </Link>
        <Link to="/admin/table" className="rhs-quick-nav-item">
          Manage permits
        </Link>
        <Link to="/admin/cases" className="rhs-quick-nav-item">
          All cases
        </Link>
        <Link to="/permit-history" className="rhs-quick-nav-item">
          Permit history
        </Link>
        <Link to="/vendor-portal" className="rhs-quick-nav-item">
          Vendor portal
        </Link>
      </nav>

      <GlobalSearch className="rhs-mb-md" />

      {error && (
        <div className="rhs-error-panel" role="alert">
          <strong>Unable to load operations summary</strong>
          <p className="rhs-muted">{error}</p>
          <p className="rhs-muted text-sm">
            Confirm GovOps entitlement and sign in with the same Firebase user as SD Panthers.
          </p>
        </div>
      )}

      {loading && (
        <div className="rhs-loading" role="status">
          <span className="rhs-spinner" aria-hidden="true" />
          Loading operations data from GovOps…
        </div>
      )}

      {!loading && isEmpty && !error && (
        <div className="rhs-empty-state">
          <h2 className="rhs-card-title">No operational data yet</h2>
          <p className="rhs-muted">
            Seed demo records for your Firebase user, then refresh this dashboard.
          </p>
          <pre className="rhs-code-block">npm run setup:govops:demo -- &lt;your-firebase-uid&gt;</pre>
          <button type="button" className="rhs-btn" onClick={() => setShowSubmit(true)}>
            Submit a permit instead
          </button>
        </div>
      )}

      {!loading && summary && !isEmpty && (
        <>
          <OperationalKpiCards kpis={summary.kpis} />

          <WorkflowStatusBoard totals={summary.workflowStatusTotals} />

          <ExportCenter />

          <OperationalActivityTable activities={summary.recentActivity} />

          <div className="rhs-admin-charts-grid">
            <StatusBreakdownChart title="Permits by status" rows={permits} />
            <StatusBreakdownChart title="Cases by status" rows={summary.cases} />
            <StatusBreakdownChart title="Violations by status" rows={summary.violations} />
          </div>

          <section className="rhs-card">
            <h2 className="rhs-card-title">Recent cases</h2>
            {recentCases.length === 0 ? (
              <p className="rhs-muted">No cases in GovOps for this organization yet.</p>
            ) : (
              <div className="rhs-table-wrap">
                <table className="rhs-table">
                  <thead>
                    <tr>
                      <th scope="col">Case</th>
                      <th scope="col">Title</th>
                      <th scope="col">Status</th>
                      <th scope="col">Updated</th>
                      <th scope="col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentCases.map((c) => (
                      <tr key={c.id}>
                        <td className="rhs-permit-id" title={c.id}>
                          {String(c.id).slice(0, 14)}…
                        </td>
                        <td>{c.title || '—'}</td>
                        <td>{formatStatus(c.status)}</td>
                        <td>{formatTimestamp(c.updatedAt || c.createdAt)}</td>
                        <td>
                          <Link to={`/cases/${c.id}`} className="rhs-table-link">
                            Open timeline
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="rhs-card">
            <div className="rhs-card-header">
              <h2 className="rhs-card-title">Recent permits</h2>
              <button
                type="button"
                className="rhs-btn rhs-btn--secondary rhs-btn--sm"
                onClick={() => setShowSubmit((v) => !v)}
              >
                {showSubmit ? 'Hide quick submit' : 'Quick submit'}
              </button>
            </div>
            <div className="rhs-table-wrap">
              <table className="rhs-table">
                <thead>
                  <tr>
                    <th scope="col">ID</th>
                    <th scope="col">Type</th>
                    <th scope="col">Status</th>
                    <th scope="col">On map</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {permits.slice(0, 8).map((permit) => {
                    const pos = getPermitMapPosition(permit);
                    return (
                      <tr key={permit.id}>
                        <td className="rhs-permit-id" title={permit.id}>
                          {String(permit.id).slice(0, 12)}…
                        </td>
                        <td>{permit.type || '—'}</td>
                        <td>{formatStatus(permit.status)}</td>
                        <td>{pos ? 'Yes' : 'No'}</td>
                        <td className="rhs-cell-actions">
                          <Link to={`/permit-details/${permit.id}`} className="rhs-table-link">
                            Details
                          </Link>
                          {' · '}
                          <Link to="/map" state={{ permitId: permit.id }} className="rhs-table-link">
                            Map
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {showSubmit && (
              <div className="rhs-admin-quick-submit">
                <h3 className="rhs-card-title">Quick permit submit</h3>
                <PermitQuickIntakeForm
                  idPrefix="admin"
                  formData={quickForm}
                  onChange={setQuickForm}
                  onSubmit={handleQuickSubmit}
                  submitting={submitting}
                  error={submitError}
                  success={submitSuccess}
                  showOptionalFields
                  showFeeFields
                />
              </div>
            )}
          </section>
        </>
      )}
    </PageShell>
  );
};

export default AdminDashboard;
